/**
 * Handout-ready printed itinerary.
 *
 * Rather than printing the live app (controls, maps, accordions and all), this
 * builds a clean standalone document and prints it from a hidden iframe — the
 * page you're looking at is untouched. The layout is deliberately a tour-group
 * handout: a cover block with the route and the booking checklist, then one
 * page per day with a bold day banner, tonight's lodging, and a time table
 * with addresses and phone numbers a traveler can act on without the app.
 */
import { computeChecklist, STATUS_LABELS, type Placement } from "@trip/shared/engine/checklist";
import { computeTrip, type FixedRowComp, type SlotRowComp } from "@trip/shared/engine/schedule";
import { esc, fdur, fmin, t12 } from "@trip/shared/engine/time";
import { isFixed } from "@trip/shared/types";
import { store } from "../store.js";

const STATUS_MARK = ["○", "✔", "🔒"];

/** Resolve a dayHome stay id to its human label — app-managed trips align
 * stayIds with the tail of stays[]; the curated trip's s<N> ids match pin
 * numbers. Null when the id resolves to nothing (e.g. a layover city). */
function stayLabel(stayId: string): string | null {
  const def = store.def;
  if (def.stayIds?.length) {
    const tail = def.stays.length - def.stayIds.length;
    const i = def.stayIds.indexOf(stayId);
    if (i >= 0) return def.stays[tail + i]?.[1] ?? null;
  }
  const m = stayId.match(/^s(\d+)$/);
  if (m) return def.stays.find((s) => s[0] === Number(m[1]))?.[1] ?? null;
  return null;
}

function dayHTML(dayId: string, dayNo: number, trip: ReturnType<typeof computeTrip>): string {
  const def = store.def;
  const day = def.days.find((d) => d.id === dayId)!;
  const comp = trip.days[dayId];
  const idx = def.days.indexOf(day);

  const rows = day.items
    .map((it, i) => {
      const row = comp.rows[i]!;
      if (isFixed(it)) {
        const fr = row as FixedRowComp;
        const time = t12(it.t) + (fr.ovMin ? ` → ${fmin(fr.eMin!)}` : it.e ? ` → ${t12(it.e)}` : "");
        return `<tr class="fixed">
          <td class="t">${esc(time)}</td>
          <td><span class="ic">${it.ic}</span> <b>${esc(it.l)}</b>${it.n ? `<div class="note">${esc(it.n)}</div>` : ""}</td>
        </tr>`;
      }
      const r = row as SlotRowComp;
      const st = store.slots[`${dayId}_${i}`];
      if (!st || r.skipped) return "";
      const a = def.act[st.act];
      const bits = [a.addr, a.tel].filter(Boolean).map((x) => esc(x!));
      return `<tr>
        <td class="t"><b>${esc(fmin(r.startMin))}</b><div class="tend">→ ${esc(fmin(r.endMin))}</div>${
          r.travel.min >= 5 ? `<div class="trav">${r.travel.icon || "🚶"} ${r.travel.min} min</div>` : ""
        }</td>
        <td>
          <div class="lbl">${esc(it.s)}</div>
          <b>${STATUS_MARK[st.st]} ${esc(a.name)}</b> <span class="dur">${esc(fdur(st.dur))}</span>
          ${bits.length ? `<div class="note">📍 ${bits.join(" · ")}</div>` : ""}
          ${a.hours ? `<div class="note">🕒 ${esc(a.hours)}</div>` : ""}
        </td>
      </tr>`;
    })
    .join("");

  const cityLine = day.trav && idx > 0 ? `${def.days[idx - 1].loc} → ${day.loc}` : day.loc;
  const home = def.dayHome[dayId];
  const tonight = home ? stayLabel(home[1]) : null;

  return `<section class="day">
    <div class="daybanner">
      <div class="dayno">Day ${dayNo}</div>
      <div class="daymain">
        <h2>${esc(day.dow)} ${esc(day.date)} <span class="city">${esc(cityLine)}</span></h2>
        <div class="daytitle">${esc(day.title)}</div>
      </div>
    </div>
    ${day.blurb ? `<p class="blurb">${esc(day.blurb)}</p>` : ""}
    ${tonight ? `<p class="stay">🏨 Tonight: <b>${esc(tonight)}</b></p>` : ""}
    <table>${rows}</table>
    ${comp.backAtBase ? `<p class="back">🏨 Back at base ~<b>${esc(fmin(comp.backAtBase.min))}</b></p>` : ""}
    ${comp.warning ? `<p class="warn">⚠ ${esc(comp.warning)}</p>` : ""}
  </section>`;
}

function checklistHTML(placements: Record<string, Placement>): string {
  const { items } = computeChecklist(store.def, store.slots, store.checks, placements);
  if (!items.length) return "";
  return `<section class="checklist">
    <h2>Booking checklist</h2>
    <table>${items
      .map(
        (b) => `<tr>
          <td class="t">${b.checked ? "☑" : "☐"}</td>
          <td><b>${esc(b.what)}</b>${
            b.scheduled ? ` <span class="dur">${esc(b.scheduled.dayLabel)} · ${esc(b.scheduled.timeLabel)}</span>` : ""
          }${b.why ? `<div class="note">${esc(b.why)}</div>` : ""}</td>
        </tr>`
      )
      .join("")}</table>
  </section>`;
}

/** Every stay with its label, in trip order — the cover's lodging directory. */
function staysHTML(): string {
  const stays = store.def.stays;
  if (!stays.length) return "";
  return `<section class="staydir">
    <h2>Where you're staying</h2>
    <table>${stays.map((s) => `<tr><td class="t">${s[0]}</td><td><b>${esc(s[1])}</b></td></tr>`).join("")}</table>
  </section>`;
}

const STYLES = `
  @page { margin: 13mm 14mm; }
  * { box-sizing: border-box; }
  body { font: 10.5pt/1.5 "Segoe UI", -apple-system, Helvetica, Arial, sans-serif; color: #14202b; margin: 0; }
  header.cover { border-bottom: 3px solid #14202b; padding-bottom: 12px; margin-bottom: 14px; }
  header.cover h1 { font: 700 24pt/1.15 Georgia, "Times New Roman", serif; margin: 0 0 4px; }
  header.cover .sub { font-size: 11pt; color: #333; margin-bottom: 2px; }
  header.cover .route { font-size: 9.5pt; color: #555; }
  header.cover .dates { font-size: 10.5pt; font-weight: 600; margin-top: 6px; }
  .legend { font-size: 9pt; color: #555; margin: 8px 0 0; }

  section { break-inside: avoid; page-break-inside: avoid; }
  .checklist, .staydir { margin-bottom: 14px; }
  .checklist h2, .staydir h2 { font-size: 12pt; margin: 0 0 4px; border-bottom: 1.5px solid #14202b; padding-bottom: 2px; }
  .checklist td.t { width: 8mm; font-size: 12pt; }
  .staydir td.t { width: 8mm; font-weight: 700; }

  /* one day per page — a handout page someone can fold and pocket */
  .day { page-break-before: always; break-before: page; margin-bottom: 0; }
  .daybanner { display: flex; align-items: stretch; gap: 10px; border-bottom: 3px solid #14202b; padding-bottom: 6px; margin-bottom: 8px; }
  .dayno { font: 700 10pt/1 "Segoe UI", sans-serif; background: #14202b; color: #fff; border-radius: 4px;
           padding: 8px 10px; align-self: center; white-space: nowrap; }
  .daymain { min-width: 0; }
  .day h2 { font: 700 15pt/1.2 Georgia, serif; margin: 0; }
  .day h2 .city { font: 600 10.5pt "Segoe UI", sans-serif; color: #0f6f86; margin-left: 8px; }
  .daytitle { font-size: 10pt; color: #444; }
  .blurb { font-size: 9.5pt; color: #444; margin: 2px 0 4px; font-style: italic; }
  .stay { font-size: 10pt; margin: 2px 0 6px; }

  table { width: 100%; border-collapse: collapse; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  td { vertical-align: top; padding: 5px 0; border-bottom: 1px solid #dfe5ea; font-size: 10pt; }
  td.t { width: 27mm; white-space: nowrap; color: #14202b; font-size: 10pt; padding-right: 8px; }
  td.t .tend { font-size: 8.5pt; color: #667; }
  tr.fixed td { background: #f2f4f6; }
  tr.fixed td:first-child { padding-left: 4px; }
  .lbl { font-size: 7.5pt; text-transform: uppercase; letter-spacing: .6px; color: #778; }
  .dur { font-size: 9pt; color: #556; }
  .note { font-size: 9pt; color: #445; }
  .trav { font-size: 8pt; color: #778; }
  .back { font-size: 10pt; margin: 6px 0 0; }
  .warn { font-size: 9.5pt; color: #a33; margin: 4px 0 0; font-weight: 600; }
  footer { margin-top: 12px; padding-top: 6px; border-top: 1px solid #bbb; font-size: 8pt; color: #666; }
`;

/** Builds the printable document. Separated from printing so it can be
 * inspected and tested without opening a print dialog. */
export function buildPrintDocument(): string {
  const def = store.def;
  const trip = computeTrip(def, store.slots, store.dayEnds, store.fixDurs);

  // reuse the checklist's own placement rule so printed dates match the screen
  const placements: Record<string, Placement> = {};
  for (const d of def.days) {
    const c = trip.days[d.id];
    d.items.forEach((it, i) => {
      if (isFixed(it)) return;
      const st = store.slots[`${d.id}_${i}`];
      const row = c.rows[i] as SlotRowComp | undefined;
      if (!st || !row || row.skipped) return;
      placements[st.act] = { dayLabel: `${d.dow} ${d.date}`, timeLabel: fmin(row.startMin) };
    });
    (c.extra?.keys ?? []).forEach((key, k) => {
      const st = store.slots[key];
      const row = c.rows[d.items.length + k] as SlotRowComp | undefined;
      if (!st || !row || row.skipped) return;
      placements[st.act] = { dayLabel: `${d.dow} ${d.date}`, timeLabel: fmin(row.startMin) };
    });
  }

  const hero = def.hero;
  const route = hero?.route
    .map((s) => (s.via ? s.via : s.stop ? `${s.stop}${s.nights ? ` (${s.nights} nt${s.nights > 1 ? "s" : ""})` : ""}` : s.place || ""))
    .join(" ");
  const first = def.days[0];
  const last = def.days[def.days.length - 1];
  const dates = first && last ? `${first.dow} ${first.date} — ${last.dow} ${last.date}` : "";

  return `<!doctype html><html><head><meta charset="utf-8">
    <title>${esc(hero?.title ?? store.tripName)}</title><style>${STYLES}</style></head><body>
    <header class="cover">
      <h1>${esc(hero?.title ?? store.tripName)}</h1>
      ${hero?.sub ? `<div class="sub">${esc(hero.sub)}</div>` : ""}
      ${route ? `<div class="route">${esc(route)}</div>` : ""}
      ${dates ? `<div class="dates">${esc(dates)}</div>` : ""}
      <div class="legend">${STATUS_LABELS.map((l) => esc(l)).join(" · ")} · All times local.</div>
    </header>
    ${staysHTML()}
    ${checklistHTML(placements)}
    ${def.days.map((d, i) => dayHTML(d.id, i + 1, trip)).join("")}
    <footer>Printed ${esc(new Date().toLocaleDateString())} — plans may shift; the live link has the latest.</footer>
  </body></html>`;
}

export function printItinerary() {
  const doc = buildPrintDocument();
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed; right:0; bottom:0; width:0; height:0; border:0;";
  document.body.appendChild(frame);
  const w = frame.contentWindow!;
  w.document.open();
  w.document.write(doc);
  w.document.close();
  // let the iframe lay out before printing, and clean up once the dialog closes
  setTimeout(() => {
    w.focus();
    w.print();
    setTimeout(() => frame.remove(), 1000);
  }, 120);
}
